import { useContext, useState } from "react"
import { Div, Col, Row, Text } from "atomize"
import { toast } from "react-toastify"
import { DateTime } from "luxon"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import Container from "../atoms/Container"
import { Block, Divider, StepTransition } from "../school/SheetBlocks"
import { theme } from "../settings/config"
import { callFunction } from "../services/firebase.service"
import { schoolContext } from "../providers/school.provider"
import { appURL } from "../utils/calendar.utils"

// Quello che la direzione potrà fare, detto prima di darle il codice
const WHAT_THEY_GET = [
	"Vede le lezioni di tutti i maestri, e le sistema",
	"Cambia campi, listino e dati del circolo",
	"Quando vuole, il circolo diventa suo: decide chi fa cosa",
]

// Il maestro che ha portato l'app in un circolo che non dirige la propone a
// chi lo dirige. È un codice d'ingresso già scelto — da gestore, senza
// insegnare: la direzione vede e sistema tutto, ma non finisce tra i maestri
// — dentro un messaggio da mandare così com'è. Quando la direzione è
// entrata, il circolo le si passa dalla sua scheda in "Chi c'è nel
// circolo". Vedi "Chi dirige il circolo" in docs/circoli.md.
//
// Il codice si genera col bottone e non all'apertura: un codice nuovo spegne
// quello di prima, e chi apre per curiosità non deve spegnere quello appena
// mandato a un collega.
export function ProposeAppModal({ closeModal }) {
	const { selectedSchool, getSelectedSchool } = useContext(schoolContext)
	const schoolName = getSelectedSchool()?.schoolName ?? "il circolo"

	const [invite, setInvite] = useState(null)
	const [isSaving, setIsSaving] = useState(false)

	const run = async (action, fallback) => {
		setIsSaving(true)
		try {
			await action()
		} catch (err) {
			toast.error(err.message || fallback, { position: "top-center" })
		} finally {
			setIsSaving(false)
		}
	}

	const createCode = () =>
		run(async () => {
			const create = callFunction("createSchoolInvite")
			const { data } = await create({
				schoolID: selectedSchool,
				role: "manager",
				teaches: false,
			})
			setInvite(data)
		}, "Non sono riuscito a generare il codice")

	// Il circolo è suo, alla fine: il passo dei Primi passi non serve più
	const itIsMine = () =>
		run(async () => {
			const setRunByOwner = callFunction("setSchoolRunByOwner")
			await setRunByOwner({ schoolID: selectedSchool, runByOwner: true })
			closeModal()
		}, "Non sono riuscito a salvarlo")

	const message = invite
		? `Buongiorno, al circolo segniamo lezioni, presenze e incassi con inCampo. ` +
			`Vi propongo di entrare anche voi: vedete le lezioni di tutti i maestri, ` +
			`e il circolo può diventare vostro.\n\n` +
			`Registratevi su ${appURL()} scegliendo "Ho il codice di un circolo": ` +
			`codice ${invite.code}\n\n` +
			`Il codice vale fino al ${DateTime.fromISO(invite.expiresAt)
				.setLocale("it")
				.toFormat("d LLLL")}.`
		: ""

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(message)
			toast.success("Messaggio copiato", { position: "top-center" })
		} catch {
			toast.error("Non riesco a copiarlo: selezionalo e copialo a mano", {
				position: "top-center",
			})
		}
	}

	const buttons = (back, next) => (
		<Div m={{ x: "1rem" }}>
			<Row m={{ t: "1.5rem" }}>
				<Col p="0">
					<SecondaryButton
						m="auto"
						textColor={theme.colors.gunmetal}
						text={isSaving ? "Un attimo..." : back.text}
						textSize="subheader"
						disabled={isSaving}
						action={back.action}
					/>
				</Col>
				{!isSaving && (
					<Col p="0">
						<PrimaryButton
							m="auto"
							bg={theme.colors.primary}
							textColor="white"
							textSize="subheader"
							text={next.text}
							action={next.action}
						/>
					</Col>
				)}
			</Row>
		</Div>
	)

	if (invite) {
		return (
			<Container>
				<StepTransition stepKey="message" direction={1}>
					<Div textWeight="bold" textSize="title">
						Il messaggio da mandare
					</Div>
					<Text
						textSize="caption"
						textColor={theme.colors.text_light}
						m={{ t: "0.25rem" }}>
						Mandalo a chi dirige {schoolName}: alla direzione, alla segreteria. Il
						codice non si può rileggere dopo.
					</Text>

					<Block m={{ t: "1.25rem" }} p="0.875rem">
						<Text
							textSize="body"
							textColor={theme.colors.gunmetal}
							style={{ whiteSpace: "pre-line", userSelect: "text" }}>
							{message}
						</Text>
					</Block>

					<Text
						textSize="caption"
						textColor={theme.colors.text_light}
						textAlign="center"
						m={{ t: "1.25rem" }}>
						Entrerà come gestore, senza insegnare. Quando c'è, il circolo glielo passi
						dalla sua scheda in Maestri.
					</Text>
				</StepTransition>

				{buttons({ text: "Chiudi", action: closeModal }, { text: "Copia", action: copy })}
			</Container>
		)
	}

	return (
		<Container>
			<StepTransition stepKey="intro" direction={0}>
				<Div textWeight="bold" textSize="title">
					Proponi l'app alla direzione
				</Div>
				<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "0.25rem" }}>
					Chi gestisce davvero {schoolName} entra con un codice, da gestore che non
					insegna. Quando vuole, gli passi il circolo.
				</Text>

				<Block m={{ t: "1.25rem" }}>
					{WHAT_THEY_GET.map((line, i) => (
						<Div key={line}>
							{i > 0 && <Divider inset="2.1rem" />}
							<Div
								d="flex"
								align="center"
								p={{ y: "0.7rem" }}
								style={{ gap: "0.75rem" }}>
								<Div
									aria-hidden="true"
									w="22px"
									h="22px"
									rounded="circle"
									bg={theme.colors.primary_light}
									textColor={theme.colors.primary}
									textSize="caption"
									textWeight="bold"
									d="flex"
									align="center"
									justify="center"
									style={{ flexShrink: 0 }}>
									{i + 1}
								</Div>
								<Text textSize="body">{line}</Text>
							</Div>
						</Div>
					))}
				</Block>

				<Div
					m={{ t: "1.25rem" }}
					textAlign="center"
					cursor={isSaving ? "default" : "pointer"}
					onClick={() => !isSaving && itIsMine()}>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						Il circolo lo dirigi tu? <u>Allora non serve</u>
					</Text>
				</Div>
			</StepTransition>

			{buttons(
				{ text: "Indietro", action: closeModal },
				{ text: "Genera il codice", action: createCode },
			)}
		</Container>
	)
}

export default ProposeAppModal
